// Improved percentageInputUtils.ts

/**
 * Sanitizes a percentage value by removing non-numeric characters except for decimal points and negative signs
 */
export const sanitizePercentValue = (value: string): string => {
  if (value === '-') {
    return '-';
  }

  const isNegative = value.trim().startsWith('-');
  const sanitized = value.replace(/[^0-9.]/g, '');
  return isNegative ? `-${sanitized}` : sanitized;
};

/**
 * Processes the number part of a percentage value
 * Removes leading zeros, but keeps a single '0' if that's the only digit
 * Preserves negative sign if present
 */
export const processNumberPart = (value: string): string => {
  if (value === '' || value === '0' || value === '-0') return '0';

  const isNegative = value.startsWith('-');
  const absValue = isNegative ? value.substring(1) : value;
  const processed = absValue.replace(/^0+/, '') || '0';

  return isNegative ? `-${processed}` : processed;
};

/**
 * Processes the decimal part of a percentage value, limiting to specified decimal places
 */
export const processDecimalPart = (value: string, maxDecimals: number = 2): string => {
  if (value.length <= maxDecimals) return value;
  return value.slice(0, maxDecimals);
};

/**
 * Creates a formatted percentage value with % symbol at the end
 * @param value The numeric value to format
 * @param maxDecimals Maximum decimal places (default: 2)
 * @returns Formatted string with % symbol
 */
export const createFormattedPercentValue = (
  value: number | string,
  maxDecimals: number = 2,
): string => {
  const isNegative =
    typeof value === 'number' ? value < 0 : value.toString().trim().startsWith('-');

  let absValue: number | string = value;
  if (typeof value === 'number') {
    absValue = Math.abs(value);
    // Add proper rounding to fix floating-point precision issues
    absValue = (
      Math.round(absValue * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
    ).toString();
  } else {
    // Remove % symbol before sanitizing
    absValue = value.replace(/%/g, '');
    if (isNegative) {
      absValue = absValue.substring(1);
    }
  }

  const sanitizedValue = sanitizePercentValue(absValue.toString());
  let cleanedValue = '';

  if (sanitizedValue.includes('.')) {
    let [numberPart, decimalPart] = sanitizedValue.split('.');
    numberPart = processNumberPart(numberPart);
    decimalPart = processDecimalPart(decimalPart, maxDecimals);
    cleanedValue = `${numberPart}.${decimalPart}`;
  } else {
    cleanedValue = sanitizedValue === '' ? '' : processNumberPart(sanitizedValue);
  }

  if (isNegative && cleanedValue !== '') {
    cleanedValue = `-${cleanedValue}`;
  }

  return cleanedValue === '' ? '' : `${cleanedValue}%`;
};

/**
 * Handles input change for percentage fields with proper cursor positioning
 * @param e Input change event
 * @param updateState Function to update the state with the new formatted value
 */
export const handlePercentageChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  updateState: (formattedValue: string) => void,
  maxValue?: number,
): void => {
  const input = e.target;
  const cursorPosition = input.selectionStart || 0;
  const prevValue = input.value;

  if (prevValue === '-') {
    updateState('-');
    return;
  }

  // Store whether cursor was at end (excluding %)
  const wasAtEnd =
    cursorPosition >= (prevValue.endsWith('%') ? prevValue.length - 1 : prevValue.length);

  // Format the value
  const formattedValue = createFormattedPercentValue(prevValue);

  // Determine the length difference between old and new values (ignoring % symbol)
  const oldValueNoPercent = prevValue.replace(/%/g, '');
  const newValueNoPercent = formattedValue.replace(/%/g, '');
  const lengthDiff = newValueNoPercent.length - oldValueNoPercent.length;

  // Update the state with formatted value
  if (maxValue && Number(newValueNoPercent) > maxValue) {
    updateState(`${maxValue}%`);
  } else {
    updateState(formattedValue);
  }

  // Calculate new cursor position
  setTimeout(() => {
    let newPosition;

    // If cursor was at end, keep it at end (before %)
    if (wasAtEnd) {
      newPosition = formattedValue.length - 1;
    }
    // If cursor was on the % symbol, place it before the %
    else if (prevValue[cursorPosition] === '%') {
      newPosition = formattedValue.length - 1;
    }
    // Otherwise maintain relative position, adjusting for any length changes
    else {
      newPosition = Math.min(cursorPosition + lengthDiff, formattedValue.length - 1);

      // Ensure cursor is never after the % symbol
      newPosition = Math.min(newPosition, formattedValue.length - 1);
    }

    input.setSelectionRange(newPosition, newPosition);
  }, 0);
};

/**
 * Removes percentage symbol and converts to number
 * @param value Formatted percentage value (e.g., "25.5%")
 * @returns String without percentage symbol
 */
export const stripPercentSymbol = (value: string): number => {
  if (value === '') return 0;

  const cleanValue = value.replace(/%/g, '');
  return parseFloat(cleanValue);
};
